import { useParams, useActionData } from "react-router-dom";
import { useEffect, useState } from "react";
import ResetPasswordForm from "../../components/forms/ResetPasswordForm";
import getAlertTitle from "../../utils/getAlertTitle";
import { statusAlertMap } from "../../lib/Constants";
import SmallAlert from "../../components/ui/alerts/SmallAlert";
import { RedirectMessage } from "../../components/ui/RedirectMessage";
import TwoColumnAuth from "../../components/layouts/TwoColumnAuth";
import LoadingPlaceholder from "../../components/ui/loading/LoadingPlaceholder";

function ResetPasswordPage() {
  const { token } = useParams();
  const actionData = useActionData();
  const success = actionData?.success;
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [alertType, setAlertType] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (actionData?.status) {
      setLoading(false);
      setAlertType(statusAlertMap[actionData.status]);
      setMessage(actionData.message);

      // Activar redirección si la acción fue exitosa o si hay error de token inválido/expirado
      if (
        actionData?.ok ||
        (!actionData?.ok && /invalid|expired/i.test(actionData?.message || ""))
      ) {
        setShouldRedirect(true);
      }
    }
  }, [actionData]);

  // Configure redirection based on action state
  const redirectConfig = actionData?.ok
    ? { redirectPath: "/sign-in", pageName: "Sign In" }
    : { redirectPath: "/forgot-password", pageName: "Forgot Password" };

  return (
    <main className="auth-page">
      <TwoColumnAuth>
        {loading && <LoadingPlaceholder />}

        {alertType && !loading && (
          <>
            <SmallAlert
              alert={true}
              title={getAlertTitle(alertType)}
              type={alertType}
              message={message}
              style={{ marginTop: 16, marginBottom: 16 }}
            />
            {shouldRedirect && (
              <RedirectMessage
                seconds={15}
                pageName={redirectConfig.pageName}
                redirectPath={redirectConfig.redirectPath}
              />
            )}
          </>
        )}

        {!loading && !success && !shouldRedirect && (
          <ResetPasswordForm token={token} onSubmit={() => setLoading(true)} />
        )}
      </TwoColumnAuth>
    </main>
  );
}

export default ResetPasswordPage;

export async function action({ request }) {
  const { token, new_password, confirm_password } = Object.fromEntries(
    await request.formData(),
  );

  const endpointPath = "account/reset-password";
  const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
  const urlPath = apiBaseUrl.concat("/", endpointPath);
  let response;

  if (!token || !new_password || !confirm_password) {
    return {
      status: 400,
      message: "All fields are required.",
    };
  }
  if (new_password !== confirm_password) {
    return {
      status: 400,
      message:
        "Passwords do not match. Please ensure both passwords are identical.",
    };
  }

  try {
    response = await fetch(urlPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token, new_password, confirm_password }),
    });
  } catch {
    return {
      status: 500,
      message: "Network error. Please try again.",
    };
  }

  let data;
  try {
    data = await response.json();
  } catch {
    return {
      status: 500,
      message: "Unexpected server error. Please try again.",
    };
  }

  // If response is successful
  if (response.ok) {
    return {
      status: response.status,
      message: data.message || "Password updated successfully.",
      ok: true,
    };
  }

  // If token is invalid or expired
  if (/invalid|expired/i.test(data.message || "")) {
    return {
      status: response.status,
      message: `${data.message} Please resend reset link to your email.`,
      ok: false,
    };
  }

  // Any other error
  return {
    status: response.status,
    message: data.message || "Could not reset password.",
    ok: false,
  };
}
